import type { Component, ComponentProps, JSXElement } from "solid-js";
import CreditsIcon from "~/Icons/16_Icon_Credits.svg?component-solid";
import CheckedIcon from "~/Icons/24_Icon_Checked.svg?component-solid";
import ThreeDDarkIcon from "~/Icons/24_Icon_Dark_3D.svg?component-solid";
import EditImageDarkIcon from "~/Icons/24_Icon_Dark_EditImage.svg?component-solid";
import PictureDarkIcon from "~/Icons/24_Icon_Dark_Picture.svg?component-solid";
import TextDarkIcon from "~/Icons/24_Icon_Dark_TextBox.svg?component-solid";
import MinusIcon from "~/Icons/24_Icon_Minus.svg?component-solid";
import Move3dIcon from "~/Icons/24_Icon_move_3d.svg?component-solid";
import PhotoIcon from "~/Icons/24_Icon_photo.svg?component-solid";
import TextFormatIcon from "~/Icons/24_Icon_textformat.svg?component-solid";
import VideoIcon from "~/Icons/24_Icon_video.svg?component-solid";
import ThreeDWhiteIcon from "~/Icons/24_Icon_White_3D.svg?component-solid";
import EditImageWhiteIcon from "~/Icons/24_Icon_White_EditImage.svg?component-solid";
import PictureWhiteIcon from "~/Icons/24_Icon_White_Picture.svg?component-solid";
import TextWhiteIcon from "~/Icons/24_Icon_White_TextBox.svg?component-solid";
import VideoWhiteIcon from "~/Icons/24_Icon_White_VideoCall.svg?component-solid";
import DalleIcon from "~/Icons/dalleIcon.svg?component-solid";
import MidjourneyIcon from "~/Icons/midjourneyIcon.svg?component-solid";
import { UserSubscriptionPlanEnum } from "~/solidJs/shared/helpers/types";
import Icons from "~/solidJs/shared/ui/atoms/IconsComp";
import type { promocodeKind } from "../types";

export type IProduct = {
	name: string;
	description: string;
	price: number;
	months: number;
	nameId: UserSubscriptionPlanEnum;
	sorting_order: string;
	features: Record<string, string>;
	/** newtariff features with description */
	featuresV2: {
		name: string;
		description: string;
	}[];
	front_price: string;
	front_price_ru: string;
	front_price_base?: string;
	front_price_ru_base?: string;
	iconsFeatures?: ProductIconFeatures[];
	isFree?: true;
	/**used for text price e.g Custom */
	isNeedToHideCurrency?: boolean;
};
export type ProductIconFeatures = {
	text: JSXElement;
	Icon: Component<ComponentProps<"svg">>;
};
export type TariffWordWith = {
	value: string;
	IconNotSelected: Component<ComponentProps<"svg">>;
	IconSelected: Component<ComponentProps<"svg">>;
};

export type FeatureItem = {
	value: string;
	Icon: Component<ComponentProps<"svg">>;
};
export type TariffCapacity = {
	name: string;
	value: string;
};
export const tariffToTitle: Partial<Record<UserSubscriptionPlanEnum, string>> = {
	plus: "For power users",
	premium: "For creators",
	advanced: "For beginners",
	freemium: "For exploring"
};
export const tariffToGenerationAmount: Partial<Record<UserSubscriptionPlanEnum, string>> = {
	plus: "Unlimited generations",
	premium: "1000 generations / month",
	advanced: "500 generations / month",
	freemium: "20 generations / week"
};
export const tariffToWorkWith: Partial<Record<UserSubscriptionPlanEnum, TariffWordWith[]>> = {
	freemium: [
		{
			value: "Text",
			IconNotSelected: TextDarkIcon,
			IconSelected: TextWhiteIcon
		},
		{
			value: "Image",
			IconNotSelected: PictureDarkIcon,
			IconSelected: PictureWhiteIcon
		}
	],
	advanced: [
		{
			value: "Text",
			IconNotSelected: TextDarkIcon,
			IconSelected: TextWhiteIcon
		},
		{
			value: "Image",
			IconNotSelected: PictureDarkIcon,
			IconSelected: PictureWhiteIcon
		},
		{
			value: "Modify Images",
			IconNotSelected: EditImageDarkIcon,
			IconSelected: EditImageWhiteIcon
		}
	],
	premium: [
		{
			value: "Text",
			IconNotSelected: TextDarkIcon,
			IconSelected: TextWhiteIcon
		},
		{
			value: "Image",
			IconNotSelected: PictureDarkIcon,
			IconSelected: PictureWhiteIcon
		},
		{
			value: "Modify Images",
			IconNotSelected: EditImageDarkIcon,
			IconSelected: EditImageWhiteIcon
		},
		{
			value: "3D",
			IconNotSelected: ThreeDDarkIcon,
			IconSelected: ThreeDWhiteIcon
		}
	],
	plus: [
		{
			value: "Text",
			IconNotSelected: TextDarkIcon,
			IconSelected: TextWhiteIcon
		},
		{
			value: "Image",
			IconNotSelected: PictureDarkIcon,
			IconSelected: PictureWhiteIcon
		},
		{
			value: "Modify Images",
			IconNotSelected: EditImageDarkIcon,
			IconSelected: EditImageWhiteIcon
		},
		{
			value: "3D",
			IconNotSelected: ThreeDDarkIcon,
			IconSelected: ThreeDWhiteIcon
		},
		{
			value: "Video",
			IconNotSelected: VideoWhiteIcon,
			IconSelected: VideoWhiteIcon
		}
	]
};
export const tariffToWorkWithV3: Partial<
	Record<UserSubscriptionPlanEnum, { generations: FeatureItem[]; features: FeatureItem[] }>
> = {
	freemium: {
		generations: [
			{
				value: "500 credits",
				Icon: CreditsIcon
			},
			{
				value: "Buy more credits",
				Icon: MinusIcon
			}
		],
		features: [
			{
				value: "~50 images",
				Icon: PhotoIcon
			},
			{
				value: "or ~10 videos",
				Icon: VideoIcon
			},
			{
				value: "or ~2 000 text generations",
				Icon: TextFormatIcon
			},
			{
				value: "or ~700 3D models",
				Icon: Move3dIcon
			}
		]
	},
	advanced: {
		generations: [
			{
				value: "10 000 credits",
				Icon: CreditsIcon
			},
			{
				value: "Buy more credits",
				Icon: CheckedIcon
			}
		],
		features: [
			{
				value: "~1 000 images",
				Icon: PhotoIcon
			},
			{
				value: "or ~100 videos",
				Icon: VideoIcon
			},
			{
				value: "or ~5 000 text generations",
				Icon: TextFormatIcon
			},
			{
				value: "or ~1 500 3D models",
				Icon: Move3dIcon
			}
		]
	},
	premium: {
		generations: [
			{
				value: "60 000 credits",
				Icon: CreditsIcon
			},
			{
				value: "Buy more credits",
				Icon: CheckedIcon
			}
		],
		features: [
			{
				value: "~3 000 images",
				Icon: PhotoIcon
			},
			{
				value: "or ~200 videos",
				Icon: VideoIcon
			},
			{
				value: "or ~10 000 text generations",
				Icon: TextFormatIcon
			},
			{
				value: "or ~3 000 3D models",
				Icon: Move3dIcon
			}
		]
	},
	plus: {
		generations: [
			{
				value: "60 000 credits",
				Icon: CreditsIcon
			},
			{
				value: "Buy more credits",
				Icon: CheckedIcon
			}
		],
		features: [
			{
				value: "~3 000 images",
				Icon: PhotoIcon
			},
			{
				value: "or ~200 videos",
				Icon: VideoIcon
			},
			{
				value: "or ~10 000 text generations",
				Icon: TextFormatIcon
			},
			{
				value: "or ~3 000 3D models",
				Icon: Move3dIcon
			}
		]
	}
};

export const maxWorkWidthLength = Object.values(tariffToWorkWith).reduce(
	(max, tariff) => Math.max(max, tariff.length),
	0
);

export const tariffToCapacity: Partial<Record<UserSubscriptionPlanEnum, TariffCapacity[]>> = {
	enterprise: [
		{
			name: "Generation",
			value: "Unlimited"
		},
		{
			name: "AI Tools",
			value: "Unlimited"
		},
		{
			name: "Onboarding",
			value: "from 5 seats"
		}
	],
	freemium: [
		{
			name: "Generation",
			value: "20/week"
		},
		{
			name: "AI Tools",
			value: "Limited"
		},
		{
			name: "Projects",
			value: "0"
		}
	],
	advanced: [
		{
			name: "Generation",
			value: "500/month"
		},
		{
			name: "AI Tools",
			value: "Limited"
		},
		{
			name: "Projects",
			value: "3"
		}
	],
	premium: [
		{
			name: "Generation",
			value: "1000/month"
		},
		{
			name: "AI Tools",
			value: "Limited"
		},
		{
			name: "Projects",
			value: "5"
		}
	],
	plus: [
		{
			name: "Generation",
			value: "Unlimited"
		},
		{
			name: "AI Tools",
			value: "Unlimited"
		},
		{
			name: "Projects",
			value: "7"
		}
	]
};
export const tariffToIncludesv2: Partial<Record<UserSubscriptionPlanEnum, string>> = {
	[UserSubscriptionPlanEnum.Legacy_Premium]: "Advanced",
	[UserSubscriptionPlanEnum.Legacy_Advanced]: "Free",
	[UserSubscriptionPlanEnum.Legacy_Plus]: "Premium"
};
export const tariffToIncludesv3: Partial<Record<UserSubscriptionPlanEnum, string>> = {
	[UserSubscriptionPlanEnum.Starter]: "Free",
	[UserSubscriptionPlanEnum.Pro]: "Starter",
	[UserSubscriptionPlanEnum.Teams]: "Pro",
	[UserSubscriptionPlanEnum.Enterprise]: "Teams"
};
export const tariffToIncludesv1: Partial<Record<UserSubscriptionPlanEnum, string[]>> = {
	[UserSubscriptionPlanEnum.Legacy_Premium]: ["Advanced"],
	[UserSubscriptionPlanEnum.Legacy_Plus]: ["Advanced", "Premium"]
};
export const tariffToIconFeatures: Partial<
	Record<UserSubscriptionPlanEnum, ProductIconFeatures[]>
> = {
	starter: [
		{
			text: "DALLE-3 100/Month",
			Icon: DalleIcon
		},
		{
			text: "GPT-4o",
			Icon: DalleIcon
		}
	],
	pro: [
		{
			text: "DALLE-3 Access \n100/Day",
			Icon: DalleIcon
		},
		{
			text: "Stable Diffusion 3 \n100/Day",
			Icon: Icons.stability.default
		},
		{
			text: "RunwayML: 20/Month",
			Icon: Icons.runway.default
		}
	],
	premium: [
		{
			text: "DALLE-3 100/Month",
			Icon: DalleIcon
		},
		{
			text: "GPT-4o",
			Icon: DalleIcon
		}
	],
	plus: [
		{
			text: "DALLE-3 Access \n100/Day",
			Icon: DalleIcon
		},
		{
			text: "Stable Diffusion 3 \n100/Day",
			Icon: Icons.stability.default
		},
		{
			text: "RunwayML: 20/Month",
			Icon: Icons.runway.default
		}
	],
	enterprise: [
		{
			text: "Midjourney",
			Icon: MidjourneyIcon
		},
		{
			text: "DALL-E 3 and GPT-4o",
			Icon: DalleIcon
		},
		{
			text: "RunwayML",
			Icon: Icons.runway.default
		}
	]
};
export type IProductList = Record<string, { front_label: string; products: IProduct[] }>;

// New types for the refactored pricing structure
export type IPlanFeature = {
	name: string;
	description: string;
};

export type IPlanCost = {
	api_label: string;
	price: string;
	price_ru: string;
	price_base: string;
	price_base_ru: string;
};

export type IPlan = {
	name: UserSubscriptionPlanEnum;
	title: string;
	description: string;
	features: IPlanFeature[];
	costs: IPlanCost[];
	sorting_order: number;
	is_free: boolean;
	count_credits: number;
	// Optional fields for backward compatibility
	nameId?: UserSubscriptionPlanEnum;
	iconsFeatures?: ProductIconFeatures[];
	featuresV2?: IPlanFeature[];
};

export type IBaseCostGeneration = {
	type: "text" | "image" | "video";
	cost: number;
};

export type INewPricingResponse = {
	plans: IPlan[];
	base_cost_generation: IBaseCostGeneration[];
};

// Helper function to find active cost by interval
export const getActiveCostByInterval = (
	costs: IPlanCost[],
	activeInterval: string
): IPlanCost | undefined => {
	// Try to find exact match first
	const exactMatch = costs.find((cost) => cost.api_label.includes(`_${activeInterval}`));
	if (exactMatch) return exactMatch;

	// Fallback logic for different interval formats
	const intervalMappings: Record<string, string[]> = {
		"1mo": ["1mo", "monthly", "1month"],
		"6mo": ["6mo", "6month", "6months"],
		"12mo": ["12mo", "annual", "yearly", "12month", "12months"],
		monthly: ["1mo", "monthly", "1month"],
		annual: ["12mo", "annual", "yearly", "12month", "12months"]
	};

	const possibleMatches = intervalMappings[activeInterval] || [activeInterval];

	for (const match of possibleMatches) {
		const found = costs.find((cost) =>
			cost.api_label.toLowerCase().includes(match.toLowerCase())
		);
		if (found) return found;
	}

	// If no match found, return first available cost as fallback
	return costs[0];
};

// Helper function to extract interval from cost api_label
export const extractIntervalFromApiLabel = (apiLabel: string): string => {
	const match = apiLabel.split("_").pop();
	return match ? match : "1mo";
};

/**
 * @description if the kyes of response is null, the promo code is incorrect
 */
export type PromoCodesVerifyResponse = {
	kind: promocodeKind | null;
	percent_off: number | null;
};
